import type { QueryKey } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { TaskSnapshot, TaskStatus } from '../api/tasks';
import { TaskStatus as TaskStatusEnum } from '../api/tasks';
import { queryClient } from '../config/react-query';
import { useTasks } from '../hooks/use-tasks';
import { useCancelTaskMutation } from '../pages/tasks/providers/use-task-mutations';
import { formatProgress } from '../pages/tasks/task-utils';
import { useNotifications } from '../utils/notifications';
import {
  hasActiveNotification,
  hasBeenNotified,
  markActiveNotification,
  markManuallyClosed,
  markNotified,
  removeActiveNotification,
  updateActiveNotification,
  wasManuallyClosed,
} from '../utils/task-notification-storage';

// Constants
const NOTIFICATION_DURATION = {
  START: 3,
  SUCCESS: 4.5,
  ERROR: 6,
  WARNING: 4,
  RUNNING: 0, // Persistent
} as const;

const TASK_AGE_THRESHOLD_HOURS = 24;
const NOTIFICATION_SEPARATOR = ' • ';

type TaskWithMeta = TaskSnapshot & { title?: string; description?: string };

const mapStatus = (tasks: TaskSnapshot[]) => {
  const map = new Map<string, TaskStatus>();
  tasks.forEach(task => map.set(task.id, task.status));
  return map;
};

type TaskConfig = {
  startMessage: string;
  successMessage: string;
  cacheInvalidationKeys: QueryKey[];
};

type TaskConfigMap = Record<string, TaskConfig>;

const TASK_CONFIG: TaskConfigMap = {
  sf_position_import: {
    startMessage: 'Importing positions from SuccessFactors...',
    successMessage: 'Positions imported successfully',
    cacheInvalidationKeys: [['positions']],
  },
  ai_enrichment: {
    startMessage: 'Enriching positions...',
    successMessage: 'Positions enriched successfully',
    cacheInvalidationKeys: [['positions']],
  },
  benchmark_generate: {
    startMessage: 'Generating benchmarks...',
    successMessage: 'Benchmarks generated successfully',
    cacheInvalidationKeys: [['benchmarks']],
  },
  benchmark_upload: {
    startMessage: 'Uploading benchmarks to SuccessFactors...',
    successMessage: 'Benchmarks uploaded successfully',
    cacheInvalidationKeys: [['benchmarks']],
  },
} as const;

/**
 * Check if a task should be skipped for notifications based on age
 * Skip if finishedAt exists and is >24 hours old
 */
const shouldSkipTaskForAge = (task: TaskSnapshot): boolean => {
  if (!task.finishedAt) return false;

  const finishedTime = Date.parse(task.finishedAt);
  const now = Date.now();
  const hoursSinceFinished = (now - finishedTime) / (1000 * 60 * 60);

  return hoursSinceFinished > TASK_AGE_THRESHOLD_HOURS;
};

const getTaskConfig = (taskType: string): TaskConfig | undefined => {
  return TASK_CONFIG[taskType];
};

const invalidateForTask = (task: TaskWithMeta) => {
  const config = getTaskConfig(task.type);
  if (!config) {
    // unknown task type; log but do not invalidate to avoid surprises
    console.debug?.('TasksWatcher: no configuration for task type', task.type);
    return;
  }
  config.cacheInvalidationKeys.forEach(key => {
    queryClient().invalidateQueries({ queryKey: key });
  });
};

const getTaskStartMessage = (taskType: string): string => {
  return getTaskConfig(taskType)?.startMessage ?? 'Task started';
};

const getTaskCompletionMessage = (taskType: string): string => {
  return getTaskConfig(taskType)?.successMessage ?? 'Task completed';
};

const getRunningNotificationKey = (taskId: string) => {
  return `task-${taskId}-running`;
};

const getStatusNotificationKey = (taskId: string) => {
  return `task-${taskId}-status`;
};

const getStartNotificationKey = (taskId: string) => {
  return `task-${taskId}-start`;
};

// Cancel button component for better reusability and styling consistency
interface CancelButtonProps {
  onClick: () => void;
  disabled: boolean;
}

const CancelButton = ({ onClick, disabled }: CancelButtonProps) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '4px 8px',
      background: '#ff4d4f',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '12px',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {disabled ? 'Canceling...' : 'Cancel'}
  </button>
);

/**
 * Create a notification for a running task with progress information
 */
const createRunningTaskNotification = (
  task: TaskWithMeta,
  cancelMutation: ReturnType<typeof useCancelTaskMutation>,
  notify: ReturnType<typeof useNotifications>['notify'],
) => {
  const notificationKey = getRunningNotificationKey(task.id);
  const progress = formatProgress(task);
  const progressText = progress !== undefined ? `${progress}%` : '';
  const stageText = task.progress?.stage || '';

  const descriptionParts = [stageText, progressText, task.description].filter(Boolean);

  notify({
    key: notificationKey,
    title: `${task.title ?? task.type} (${task.status})`,
    description: descriptionParts.join(NOTIFICATION_SEPARATOR),
    role: 'status',
    duration: NOTIFICATION_DURATION.RUNNING,
    onClose: () => {
      markManuallyClosed(task.id);
    },
    actions: <CancelButton onClick={() => cancelMutation.mutate(task.id)} disabled={cancelMutation.isPending} />,
  });
};

/**
 * Handle notifications for task status transitions
 */
const handleTaskStatusTransition = (
  task: TaskWithMeta,
  newStatus: TaskStatus,
  closeRunningNotification: (taskId: string) => void,
  notifySuccess: ReturnType<typeof useNotifications>['notifySuccess'],
  notifyError: ReturnType<typeof useNotifications>['notifyError'],
  notifyWarning: ReturnType<typeof useNotifications>['notifyWarning'],
) => {
  const key = getStatusNotificationKey(task.id);
  closeRunningNotification(task.id);

  switch (newStatus) {
    case TaskStatusEnum.Succeeded:
      if (!hasBeenNotified(task.id, TaskStatusEnum.Succeeded)) {
        notifySuccess({
          key,
          title: getTaskCompletionMessage(task.type),
          description: task.description,
          duration: NOTIFICATION_DURATION.SUCCESS,
        });
        markNotified(task.id, TaskStatusEnum.Succeeded);
      }
      invalidateForTask(task);
      break;

    case TaskStatusEnum.Failed:
      if (!hasBeenNotified(task.id, TaskStatusEnum.Failed)) {
        notifyError({
          key,
          title: 'Task failed',
          description: task.error ?? 'Task failed',
          duration: NOTIFICATION_DURATION.ERROR,
        });
        markNotified(task.id, TaskStatusEnum.Failed);
      }
      invalidateForTask(task);
      break;

    case TaskStatusEnum.Canceled:
      if (!hasBeenNotified(task.id, TaskStatusEnum.Canceled)) {
        notifyWarning({
          key,
          title: 'Task canceled',
          duration: NOTIFICATION_DURATION.WARNING,
        });
        markNotified(task.id, TaskStatusEnum.Canceled);
      }
      invalidateForTask(task);
      break;
  }
};

/**
 * Handle notifications for running tasks
 */
const handleRunningTask = (
  task: TaskWithMeta,
  cancelMutation: ReturnType<typeof useCancelTaskMutation>,
  notify: ReturnType<typeof useNotifications>['notify'],
) => {
  if (wasManuallyClosed(task.id)) {
    return;
  }

  createRunningTaskNotification(task, cancelMutation, notify);

  if (!hasActiveNotification(task.id)) {
    markActiveNotification(task.id);
  } else {
    updateActiveNotification(task.id);
  }
};

/**
 * Handle notifications for new tasks
 */
const handleNewTask = (task: TaskWithMeta, notifyInfo: ReturnType<typeof useNotifications>['notifyInfo']) => {
  notifyInfo({
    key: getStartNotificationKey(task.id),
    title: task.title ?? getTaskStartMessage(task.type),
    description: task.description,
    duration: NOTIFICATION_DURATION.START,
  });
};

export const TasksWatcher = () => {
  const { data: tasks = [] } = useTasks({ driver: true });
  const { notifySuccess, notifyError, notifyInfo, notifyWarning, notify, destroyNotification } = useNotifications();
  const cancelMutation = useCancelTaskMutation();
  const prevStatusRef = useRef<Map<string, TaskStatus>>(new Map());

  const closeRunningNotification = (taskId: string) => {
    const key = getRunningNotificationKey(taskId);
    destroyNotification(key);
    removeActiveNotification(taskId);
  };

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const currStatus = mapStatus(tasks);

    (tasks as TaskWithMeta[]).forEach(task => {
      const statusBefore = prevStatus.get(task.id);
      const statusAfter = currStatus.get(task.id);

      if (shouldSkipTaskForAge(task)) {
        return;
      }

      if (!statusBefore && statusAfter && !hasBeenNotified(task.id, statusAfter)) {
        handleNewTask(task, notifyInfo);
        return;
      }

      if (statusAfter === TaskStatusEnum.Running) {
        handleRunningTask(task, cancelMutation, notify);
        return;
      }

      if (statusBefore && statusAfter && statusBefore !== statusAfter) {
        handleTaskStatusTransition(
          task,
          statusAfter,
          closeRunningNotification,
          notifySuccess,
          notifyError,
          notifyWarning,
        );
      }
    });

    prevStatusRef.current = currStatus;
  }, [tasks, notifySuccess, notifyError, notifyInfo, notifyWarning, notify, destroyNotification, cancelMutation]);

  return null;
};
